/**
 * Browser compatibility polyfills.
 *
 * Patches missing APIs on older Safari (< 17.2), Firefox (< 121), and
 * browsers without TC39 Stage-4 `Map.getOrInsertComputed`.
 * Must be the very first import in main.tsx so that third-party code
 * (pdfjs-dist, PostHog SDK, etc.) finds these globals already in place.
 */

// Side-effect module — export {} makes this a TS module so declare global works
export {};

// ---------- Type augmentations (co-located with the polyfill code) ----------
declare global {
  interface PromiseConstructor {
    withResolvers<T>(): {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    };
  }

  interface URLConstructor {
    parse(url: string, base?: string): URL | null;
  }

  interface Map<K, V> {
    getOrInsertComputed(key: K, callbackFn: (key: K) => V): V;
  }
}

// Promise.withResolvers — Safari < 17.2, Firefox < 121
// pdfjs-dist v5.4 uses this internally for image decoding
if (typeof Promise.withResolvers === 'undefined') {
  // Promise executor is synchronous — resolve/reject are always assigned before use
  Promise.withResolvers = function <T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// URL.parse — Safari < 17
// pdfjs-dist or bundled dependencies use this as a safe URL parser
if (typeof URL.parse === 'undefined') {
  URL.parse = function (url: string, base?: string): URL | null {
    try {
      return new URL(url, base);
    } catch {
      return null;
    }
  };
}

// crypto.randomUUID — Safari < 15.4
// Used directly in 6 legalworkspace components (V3ChatInterface, SaveToResearchDialog, etc.)
if (
  typeof crypto !== 'undefined' &&
  typeof crypto.getRandomValues !== 'undefined' &&
  typeof crypto.randomUUID === 'undefined'
) {
  crypto.randomUUID = function (): `${string}-${string}-${string}-${string}-${string}` {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // RFC 4122 v4: set version bits (0100) and variant bits (10xx)
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` as `${string}-${string}-${string}-${string}-${string}`;
  };
}

// Map.prototype.getOrInsertComputed — Chrome 133+ only (TC39 Stage 4, 2025)
// Some bundled dependency triggers this; not available in Safari/Firefox yet
if (typeof Map.prototype.getOrInsertComputed === 'undefined') {
  Map.prototype.getOrInsertComputed = function <K, V>(
    key: K,
    callbackFn: (key: K) => V,
  ): V {
    if (this.has(key)) {
      return this.get(key) as V;
    }
    const value = callbackFn(key);
    this.set(key, value);
    return value;
  };
}
